import { Landmark } from "../common/arc_devices";
import { Pose } from "../common/geometry";
import { MapMetadata, Mission, Waypoint } from "../common/navigation";
import {
  SERVICE_CREATE_LANDMARKS,
  SERVICE_GET_LANDMARKS,
  SERVICE_GET_MISSION,
  SERVICE_LIST_MISSIONS,
  SERVICE_LIST_MAP,
  SERVICE_MAP_SYS_START,
  SERVICE_MAP_SYS_STOP,
  SERVICE_MAP_VIEW,
  SERVICE_TO_LANDMARK,
} from "../state";
import { CheckResponseOrThrow, servicesFromProtocols } from "./base";

export type NavigationResponse = {
  status: number;
  error_msg?: string;
  [key: string]: any;
};

const checkResponseOrThrow: CheckResponseOrThrow<NavigationResponse> = (
  response
) => {
  const { status, error_msg = "Unknown Error" } = response;
  if (status) throw new Error(error_msg);
  return response;
};

const NAV_DEFAULT_RES_TRANSFORMER = <T = any>(
  response: NavigationResponse
): T => {
  return response.data;
};

export type CreateLandmarksOptions = {
  map_id: string;
  name: string;
  pose: Pose;
  type?: string;
};

export type StartMapNavSystemOptions = {
  map_id?: string;
};

export type ConfigMapNavSystemOptions = {} & StartMapNavSystemOptions;

const startMapNavSystem = ({ map_id }: StartMapNavSystemOptions) => ({
  service: SERVICE_MAP_SYS_START,
  data: { map_id },
});

const stopMapNavSystem = () => ({ service: SERVICE_MAP_SYS_STOP, data: {} });

const NavigationServiceProtocols = {
  getMapList: {
    makeReq: () => ({ service: SERVICE_LIST_MAP, data: {} }),
    transformRes: NAV_DEFAULT_RES_TRANSFORMER<{ id: string; name: string }[]>,
  },
  getMapMetaData: {
    makeReq: (map_id: string) => ({
      service: SERVICE_MAP_VIEW,
      data: { map_id },
    }),
    transformRes: ({
      map_graphic_data,
      map_meta_data,
    }: NavigationResponse): {
      map_graphic_data: string;
      map_meta_data: MapMetadata;
    } => {
      return { map_graphic_data, map_meta_data };
    },
  },
  getLandMarks: {
    makeReq: (map_id: string, landmark_id: string) => ({
      service: SERVICE_GET_LANDMARKS,
      data: { map_id, landmark_id },
    }),
    transformRes: ({ data }: NavigationResponse) => {
      return data as Landmark[];
    },
  },
  toLandmarks: {
    makeReq: (map_id: string, landmark_id: string) => ({
      service: SERVICE_TO_LANDMARK,
      data: { map_id, landmark_id },
    }),
    transformRes: NAV_DEFAULT_RES_TRANSFORMER,
  },
  createLandmarks: {
    makeReq: ({ map_id, name, pose, type = "" }: CreateLandmarksOptions) => ({
      service: SERVICE_CREATE_LANDMARKS,
      data: { map_id, name, pose, type },
    }),
    transformRes: NAV_DEFAULT_RES_TRANSFORMER,
  },
  configMapNavSystem: {
    makeReq: (
      mode: "START" | "STOP",
      options: ConfigMapNavSystemOptions = {}
    ) => {
      switch (mode) {
        case "START":
          return startMapNavSystem(options);
        case "STOP":
          return stopMapNavSystem();
        default:
          throw new Error(
            'configMapNavSystem: invalid mode. expects "START" or "STOP"'
          );
      }
    },
    transformRes: NAV_DEFAULT_RES_TRANSFORMER,
  },
  startMapNavSystem: {
    makeReq: ({ map_id = "" }: ConfigMapNavSystemOptions) => {
      if (!map_id)
        throw new Error("startMapNavSystem requires a map_id in its option");
      return startMapNavSystem({ map_id });
    },
    transformRes: NAV_DEFAULT_RES_TRANSFORMER,
  },
  stopMapNavSystem: {
    makeReq: stopMapNavSystem,
    transformRes: NAV_DEFAULT_RES_TRANSFORMER,
  },
  getMissions: {
    makeReq: () => {
      return { service: SERVICE_LIST_MISSIONS, data: null };
    },
    transformRes: ({ missions }: NavigationResponse) => missions as Mission[],
  },
  getMission: {
    makeReq: (mission_id: string) => {
      return { service: SERVICE_GET_MISSION, data: { mission_id } };
    },
    transformRes: ({ mission }: NavigationResponse) => mission as Mission,
  },
};

const makeNavigationServices = servicesFromProtocols<
  NavigationResponse,
  typeof NavigationServiceProtocols
>(NavigationServiceProtocols, checkResponseOrThrow);

export type NavigationServices = ReturnType<typeof makeNavigationServices>;

export default makeNavigationServices;
